/**
 * One short message at the bottom of the screen.
 *
 * Errors reach here from the query cache, which only reports a failure once
 * the retries are exhausted -- so a toast means the app has actually given
 * up, not that a request blinked. Anything still being retried is covered by
 * the spinner on the stop it belongs to.
 *
 * Deliberately one message rather than a stack. Every stop on the board fails
 * for the same reason at the same moment, and five copies of "no contact"
 * says nothing the first one did not.
 */
import { useSyncExternalStore } from 'react';

export type Toast = { id: number; message: string };

/** Long enough to read twice, short enough not to sit on top of the board. */
const VISIBLE_MS = 6_000;

/**
 * How long a dismissed message stays gone.
 *
 * Without this, a sustained outage re-raises the same toast on every refresh,
 * so dismissing it does nothing and the board flashes a complaint at you
 * every thirty seconds. Dismissing means "I know", and it is taken to mean
 * that for a couple of minutes.
 */
const MUTE_MS = 120_000;

let current: Toast | null = null;
let sequence = 0;
let hideTimer: ReturnType<typeof setTimeout> | undefined;
const mutedUntil = new Map<string, number>();
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

const snapshot = () => current;

export function showToast(message: string) {
  const muted = mutedUntil.get(message);
  if (muted !== undefined && Date.now() < muted) return;

  clearTimeout(hideTimer);
  // The same message arriving again restarts the clock rather than stacking a
  // second copy or flickering the first one out and back in.
  if (current?.message !== message) {
    current = { id: ++sequence, message };
    emit();
  }
  hideTimer = setTimeout(() => {
    current = null;
    emit();
  }, VISIBLE_MS);
}

export function dismissToast() {
  if (current) mutedUntil.set(current.message, Date.now() + MUTE_MS);
  clearTimeout(hideTimer);
  current = null;
  emit();
}

export function useToast(): Toast | null {
  return useSyncExternalStore(subscribe, snapshot, () => null);
}
